import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconCamera,
  IconPlayerPlay,
  IconRefresh,
} from 'design/icons';

import { fetchWithAuth } from '../utils/auth';
import CardCamSaleGallery from './CardCamSaleGallery';
import CardCamCostEstimate from './CardCamCostEstimate';
import './CardCamLive.css';

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function formatGBP(value) {
  return value == null ? '—' : GBP.format(value);
}

function formatTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : DATE_TIME.format(date);
}

function sellerValue(value) {
  return String(value || '').trim().toLowerCase();
}

function filterValues(values, normalize = (value) => value) {
  return [...new Set(values
    .filter((value) => typeof value === 'string')
    .map((value) => normalize(value.trim()))
    .filter(Boolean))].sort();
}

function initialFilter(name) {
  return new URLSearchParams(window.location.search).get(name)?.trim() || '';
}

async function responseData(response) {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(
      payload?.error?.message || 'The CardCam live feed is unavailable.'
    );
    error.status = response.status;
    throw error;
  }
  return payload?.data;
}

function CaptureImage({ captureID, label }) {
  const [source, setSource] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!captureID) return undefined;
    let active = true;
    let objectURL = '';
    setSource('');
    setError('');

    fetchWithAuth(
      `/api/cardcam/v2/captures/image?id=${encodeURIComponent(captureID)}`
    )
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(
            payload?.error?.message || 'Capture image is unavailable.'
          );
        }
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectURL = URL.createObjectURL(blob);
        setSource(objectURL);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      });

    return () => {
      active = false;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [captureID]);

  if (error) {
    return (
      <div className="cardcam-image-state">
        <IconCamera size={30} />
        <span>{error}</span>
      </div>
    );
  }
  if (!source) {
    return (
      <div className="cardcam-image-state cardcam-image-loading">
        <IconCamera size={30} />
        <span>Loading capture…</span>
      </div>
    );
  }
  return <img className="cardcam-image" src={source} alt={label} />;
}

function Metric({ label, value, detail, tone = '' }) {
  return (
    <div className={`cardcam-metric ${tone ? `cardcam-metric-${tone}` : ''}`}>
      <span className="rc-label">{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="cardcam-empty">
      <IconCamera size={40} />
      <h2>Waiting for CardCam</h2>
      <p>New captures will appear here automatically during the stream.</p>
    </div>
  );
}

export default function CardCamLive() {
  const [snapshot, setSnapshot] = useState({
    cards: [],
    viewer: null,
    serverTime: null,
    filterOptions: { deviceIDs: [], sellerUsernames: [] },
  });
  const [selectedID, setSelectedID] = useState('');
  const [captureFilter, setCaptureFilter] = useState(() => {
    const value = initialFilter('captureID');
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value) ? value.toLowerCase() : '';
  });
  const [deviceFilter, setDeviceFilter] = useState(() => initialFilter('deviceID'));
  const [sellerFilter, setSellerFilter] = useState(
    () => sellerValue(initialFilter('sellerUsername'))
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [autoFollow, setAutoFollow] = useState(() => !initialFilter('captureID'));
  const [acquisitionDraft, setAcquisitionDraft] = useState('');
  const [gradingDraft, setGradingDraft] = useState('');
  const [confirmNote, setConfirmNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [saleHistory, setSaleHistory] = useState({
    analytics: null,
    loading: false,
    error: '',
  });
  const autoFollowRef = useRef(true);

  useEffect(() => {
    autoFollowRef.current = autoFollow;
  }, [autoFollow]);

  useEffect(() => {
    let active = true;
    let requestController = null;
    let refreshTimer = null;
    let consecutiveFailures = 0;
    setLoading(true);
    setError('');

    const query = new URLSearchParams({ limit: '100' });
    if (sellerFilter) query.set('sellerUsername', sellerFilter);
    if (deviceFilter) query.set('deviceID', deviceFilter);
    if (captureFilter) query.set('captureID', captureFilter);

    async function refresh() {
      requestController = new AbortController();
      let nextRefreshMilliseconds = 2000;
      try {
        const response = await fetchWithAuth(
          `/api/cardcam/v2/captures/live?${query}`,
          { signal: requestController.signal }
        );
        const data = await responseData(response);
        if (!active) return;
        setSnapshot((current) => ({
          ...data,
          // Registered choices must remain available even when an account has
          // no rows in the current page or a metadata read briefly fails.
          filterOptions: {
            deviceIDs: filterValues([
              ...(current.filterOptions?.deviceIDs || []),
              ...(data.filterOptions?.deviceIDs || []),
              ...(data.cards || []).map((card) => card.deviceID),
            ]),
            sellerUsernames: filterValues([
              ...(current.filterOptions?.sellerUsernames || []),
              ...(data.filterOptions?.sellerUsernames || []),
              ...(data.cards || []).map((card) => card.sellerUsername),
            ], sellerValue),
          },
        }));
        setError('');
        consecutiveFailures = 0;
        setSelectedID((current) => {
          const cards = data?.cards || [];
          if (!cards.length) return '';
          if (
            autoFollowRef.current ||
            !cards.some((card) => card.id === current)
          ) {
            return cards[0].id;
          }
          return current;
        });
      } catch (requestError) {
        if (
          active &&
          requestError?.name !== 'AbortError'
        ) {
          consecutiveFailures += 1;
          setError(requestError.message);
          // An expired staff token was previously hammering the live endpoint
          // every two seconds forever. Keep successful live updates fast, but
          // back off failures and give authentication time to recover.
          nextRefreshMilliseconds =
            requestError?.status === 401 ||
            requestError?.status === 403
              ? 30_000
              : Math.min(
                  2_000 * (2 ** Math.min(consecutiveFailures, 4)),
                  30_000
                );
        }
      } finally {
        if (active) {
          setLoading(false);
          refreshTimer = window.setTimeout(
            refresh,
            nextRefreshMilliseconds
          );
        }
      }
    }

    refresh();
    return () => {
      active = false;
      window.clearTimeout(refreshTimer);
      requestController?.abort();
    };
  }, [deviceFilter, sellerFilter, captureFilter]);

  const devices = useMemo(
    () =>
      filterValues([
        ...(snapshot.filterOptions?.deviceIDs || []),
        ...snapshot.cards.map((card) => card.deviceID),
        deviceFilter,
      ]),
    [deviceFilter, snapshot.cards, snapshot.filterOptions]
  );
  const sellers = useMemo(
    () =>
      filterValues([
        ...(snapshot.filterOptions?.sellerUsernames || []),
        ...snapshot.cards.map((card) => card.sellerUsername),
        sellerFilter,
      ], sellerValue),
    [sellerFilter, snapshot.cards, snapshot.filterOptions]
  );
  const visibleCards = useMemo(
    () =>
      snapshot.cards.filter(
        (card) =>
          (!deviceFilter || card.deviceID === deviceFilter) &&
          (!sellerFilter || sellerValue(card.sellerUsername) === sellerFilter)
      ),
    [deviceFilter, sellerFilter, snapshot.cards]
  );
  const selected =
    visibleCards.find((card) => card.id === selectedID) ||
    visibleCards[0] ||
    null;
  const isAdminView = Boolean(snapshot.viewer?.canSeeFinancials);
  const canConfirm = Boolean(snapshot.viewer?.canConfirm);
  const financials = selected?.financials || null;
  const acquisitionPending = [
    'purchase_history',
    'buylist',
    'estimate',
  ].includes(financials?.acquisitionProvenance?.basis) &&
    selected?.reviewStatus !== 'confirmed';
  const isProvisional = selected?.reviewStatus !== 'confirmed';
  const identityLine = selected
    ? [selected.identity.setName, selected.identity.number]
        .filter(Boolean)
        .join(' · ')
    : '';
  const gradeLine = selected
    ? selected.identity.gradingCompany
      ? `${selected.identity.gradingCompany} ${selected.identity.grade || ''}`.trim()
      : selected.identity.kind === 'raw'
        ? 'Raw card'
        : 'Unclassified'
    : '';

  useEffect(() => {
    if (!isAdminView || !selected?.id) {
      setSaleHistory({ analytics: null, loading: false, error: '' });
      return undefined;
    }

    let active = true;
    const controller = new AbortController();
    setSaleHistory((current) => ({
      ...current,
      loading: true,
      error: '',
    }));
    fetchWithAuth(
      `/api/cardcam/v2/captures/history?captureID=${encodeURIComponent(
        selected.id
      )}`,
      { signal: controller.signal }
    )
      .then(responseData)
      .then((data) => {
        if (!active) return;
        setSaleHistory({
          analytics: data?.redacted ? null : data?.analytics || null,
          loading: false,
          error: '',
        });
      })
      .catch((requestError) => {
        if (!active || requestError?.name === 'AbortError') return;
        setSaleHistory({
          analytics: null,
          loading: false,
          error: requestError.message,
        });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [isAdminView, selected?.id, selected?.revision]);

  useEffect(() => {
    setAcquisitionDraft(
      financials?.acquisitionCostGBP == null
        ? ''
        : String(financials.acquisitionCostGBP)
    );
    setGradingDraft(
      financials?.gradingCostGBP == null
        ? ''
        : String(financials.gradingCostGBP)
    );
    setConfirmNote('');
    setConfirmMessage('');
    setConfirmError('');
  }, [
    selected?.id,
    selected?.revision,
    financials?.acquisitionCostGBP,
    financials?.gradingCostGBP,
  ]);

  async function confirmFinancials(event) {
    event.preventDefault();
    if (!selected || !financials || !canConfirm) return;
    const acquisition = Number(acquisitionDraft);
    const grading = Number(gradingDraft);
    if (
      acquisitionDraft.trim() === '' ||
      !Number.isFinite(acquisition) ||
      acquisition < 0
    ) {
      setConfirmError('Enter the confirmed acquisition cost.');
      return;
    }
    if (
      gradingDraft.trim() === '' ||
      !Number.isFinite(grading) ||
      grading < 0
    ) {
      setConfirmError('Enter the grading cost, including £0 if already graded.');
      return;
    }

    const body = { expectedRevision: selected.revision };
    if (financials.acquisitionCostGBP !== acquisition) {
      body.acquisitionCostGBP = acquisition;
    }
    if (financials.gradingCostGBP !== grading) {
      body.gradingCostGBP = grading;
    }
    if (confirmNote.trim()) body.note = confirmNote.trim();

    setConfirming(true);
    setConfirmError('');
    setConfirmMessage('');
    try {
      const response = await fetchWithAuth(
        `/api/cardcam/v2/captures/${encodeURIComponent(selected.id)}/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const data = await responseData(response);
      setSnapshot((current) => ({
        ...current,
        cards: current.cards.map((card) =>
          card.id === selected.id
            ? {
                ...card,
                reviewStatus: data.reviewStatus,
                revision: data.revision,
              }
            : card
        ),
      }));
      setConfirmMessage(
        `Confirmed revision ${data.revision}. Card and grading costs remain separate.`
      );
    } catch (requestError) {
      setConfirmError(requestError.message);
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className="rc-page cardcam-page">
      <header className="rc-page-header cardcam-header">
        <div className="rc-page-header__text">
          <p className="rc-eyebrow cardcam-eyebrow">Streams</p>
          <h1 className="rc-page-title">Stream card monitor</h1>
          <p className="rc-page-description">
            Captures update every two seconds across RandCards streaming Macs.
          </p>
        </div>
        <div
          className={`cardcam-view-badge ${
            isAdminView ? 'cardcam-view-admin' : 'cardcam-view-streamer'
          }`}
        >
          {isAdminView
            ? 'Admin view · internal costs visible'
            : 'Streamer view · costs hidden'}
        </div>
      </header>

      {isAdminView ? <CardCamSaleGallery /> : null}

      <section className="cardcam-toolbar" aria-label="Live feed filters">
        <label>
          Account
          <select
            value={sellerFilter}
            onChange={(event) => { setCaptureFilter(''); setSellerFilter(event.target.value); }}
          >
            <option value="">All accounts</option>
            {sellers.map((seller) => (
              <option key={seller} value={seller}>
                {seller}
              </option>
            ))}
          </select>
        </label>
        <label>
          Computer
          <select
            value={deviceFilter}
            onChange={(event) => { setCaptureFilter(''); setDeviceFilter(event.target.value); }}
          >
            <option value="">All computers</option>
            {devices.map((device) => (
              <option key={device} value={device}>
                {device}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={autoFollow ? 'cardcam-follow-active' : ''}
          onClick={() => {
            setCaptureFilter('');
            const query = new URLSearchParams(window.location.search);
            query.delete('captureID');
            window.history.replaceState(window.history.state, '', `${window.location.pathname}${query.size ? `?${query}` : ''}`);
            setAutoFollow(true);
            if (visibleCards[0]) setSelectedID(visibleCards[0].id);
          }}
        >
          {autoFollow ? <IconRefresh size={15} /> : <IconPlayerPlay size={15} />}
          {captureFilter ? 'Back to live' : autoFollow ? 'Following live' : 'Resume live'}
        </button>
        <span className="cardcam-updated">
          Last update {formatTime(snapshot.serverTime)}
        </span>
      </section>

      {error ? <div className="cardcam-error">{error}</div> : null}
      {loading ? (
        <div className="cardcam-empty">
          <IconRefresh className="cardcam-spin" size={36} />
          <h2>Connecting to CardCam…</h2>
        </div>
      ) : visibleCards.length === 0 ? (
        <EmptyFeed />
      ) : (
        <div className="cardcam-layout">
          <aside className="cardcam-queue" aria-label="Recent cards">
            <div className="cardcam-queue-heading">
              <strong>Recent cards</strong>
              <span>{visibleCards.length}</span>
            </div>
            <ol>
              {visibleCards.map((card) => (
                <li key={card.id}>
                  <button
                    type="button"
                    className={
                      card.id === selected?.id ? 'cardcam-card-selected' : ''
                    }
                    onClick={() => {
                      setSelectedID(card.id);
                      setAutoFollow(false);
                    }}
                  >
                    <span className="cardcam-card-time">
                      {formatTime(card.capturedAt)}
                    </span>
                    <strong>{card.identity.name}</strong>
                    <small>
                      {[card.identity.setName, card.identity.number]
                        .filter(Boolean)
                        .join(' · ') || 'Identity needs checking'}
                    </small>
                    <span className="cardcam-card-bottom">
                      <span>{card.sellerUsername || card.deviceID}</span>
                      <b>
                        {isAdminView && card.financials?.hammerGBP != null
                          ? `Hammer ${formatGBP(card.financials.hammerGBP)}`
                          : card.market.valueGBP != null
                            ? `Market ${formatGBP(card.market.valueGBP)}`
                            : card.market.retailGBP != null
                              ? `Retail ${formatGBP(card.market.retailGBP)}`
                              : 'No price linked'}
                      </b>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </aside>

          <article className="cardcam-detail">
            <div className="cardcam-detail-top">
              <div className="cardcam-image-wrap">
                <CaptureImage
                  captureID={selected.id}
                  label={`${selected.identity.name} CardCam capture`}
                />
              </div>
              <div className="cardcam-identity">
                <div className="cardcam-detail-meta">
                  <span>{selected.sellerUsername || 'Unknown account'}</span>
                  <span>{selected.deviceID}</span>
                  <span>
                    {selected.reviewStatus === 'confirmed'
                      ? 'Financials confirmed'
                      : `Financials unconfirmed · revision ${selected.revision}`}
                  </span>
                </div>
                <h2>{selected.identity.name}</h2>
                <p>{identityLine || 'Identity needs checking'}</p>
                <div className="cardcam-grade">{gradeLine}</div>
                <div className="cardcam-confidence">
                  Recognition{' '}
                  {selected.identity.confidence == null
                    ? '—'
                    : `${Math.round(selected.identity.confidence * 100)}%`}
                </div>
              </div>
            </div>

            {isAdminView ? <CardCamCostEstimate capture={selected} canSeeFinancials={isAdminView} /> : null}

            {['valueGBP', 'lowGBP', 'highGBP', 'retailGBP'].some((key) => typeof selected.market?.[key] === 'number' && Number.isFinite(selected.market[key])) ? <section className="cardcam-section">
              <div className="cardcam-section-heading">
                <span>Live market</span>
                <small>{selected.market.source || 'No source'}</small>
              </div>
              <div className="cardcam-metrics">
                <Metric
                  label="Market value"
                  value={formatGBP(selected.market.valueGBP)}
                  detail="Headline comp"
                />
                <Metric
                  label="Market range"
                  value={`${formatGBP(selected.market.lowGBP)} – ${formatGBP(
                    selected.market.highGBP
                  )}`}
                  detail="Low to high"
                />
                <Metric
                  label="RandCards retail"
                  value={formatGBP(selected.market.retailGBP)}
                  detail="Fallback only"
                />
              </div>
            </section> : null}

            {isAdminView ? (
              <details
                key={selected.id}
                className="cardcam-section cardcam-financial-section rc-disclosure"
                open={canConfirm}
              >
                <summary>Stored accounting records</summary>
                <div className="rc-disclosure__body">
                <p className="rc-help">
                  These are the original recorded amounts. Use Best available cost above for the current estimate.
                  A missing stored purchase amount does not mean the card has no cost estimate.
                </p>
                <div className="cardcam-section-heading">
                  <span>Original recorded costs</span>
                  <small>Hidden from streamer view</small>
                </div>
                <div className="cardcam-metrics">
                  <Metric
                    label="Recorded acquisition"
                    value={formatGBP(financials?.acquisitionCostGBP)}
                    detail={
                      financials?.acquisitionProvenance?.source
                        ? `${financials.acquisitionProvenance.source}${
                            acquisitionPending ? ' · unconfirmed' : ''
                          }`
                        : 'No confirmed acquisition recorded'
                    }
                  />
                  <Metric
                    label="Grading cost"
                    value={formatGBP(financials?.gradingCostGBP)}
                    detail={
                      selected.identity.kind === 'slab'
                        ? financials?.gradingCostGBP == null
                          ? 'Estimated grading is shown above'
                          : 'Existing recorded or policy amount'
                        : 'Raw card'
                    }
                  />
                  <Metric
                    label="Hammer"
                    value={formatGBP(financials?.hammerGBP)}
                    detail="Live sale price"
                  />
                  <Metric
                    label="Recorded net profit"
                    value={formatGBP(financials?.netProfitGBP)}
                    detail={
                      financials?.marginPercent == null
                        ? 'Waiting for complete sale data'
                        : `${isProvisional ? 'Provisional · ' : ''}${
                            financials.marginPercent.toFixed(1)
                          }% margin`
                    }
                    tone={
                      financials?.netProfitGBP == null
                        ? ''
                        : financials.netProfitGBP >= 0
                          ? 'positive'
                          : 'negative'
                    }
                  />
                </div>
                <p className="cardcam-cost-note">
                  Acquisition and grading remain separate. Tom’s downstream
                  costing keeps the card purchase cost unchanged and books
                  grading independently.
                </p>
                {canConfirm && selected.reviewStatus !== 'confirmed' ? (
                  <form
                    className="cardcam-confirm-form"
                    onSubmit={confirmFinancials}
                  >
                    <div className="cardcam-confirm-heading">
                      <strong>Tom’s final check</strong>
                      <span>Confirming revision {selected.revision}</span>
                    </div>
                    <div className="cardcam-confirm-fields">
                      <label>
                        Acquisition cost
                        <span>
                          £
                          <input
                            inputMode="decimal"
                            min="0"
                            max="1000000"
                            step="0.01"
                            type="number"
                            value={acquisitionDraft}
                            onChange={(event) =>
                              setAcquisitionDraft(event.target.value)
                            }
                            required
                          />
                        </span>
                      </label>
                      <label>
                        Grading cost
                        <span>
                          £
                          <input
                            inputMode="decimal"
                            min="0"
                            max="1000000"
                            step="0.01"
                            type="number"
                            value={gradingDraft}
                            onChange={(event) =>
                              setGradingDraft(event.target.value)
                            }
                            required
                          />
                        </span>
                      </label>
                      <label className="cardcam-confirm-note">
                        Note (optional)
                        <input
                          maxLength="2000"
                          type="text"
                          value={confirmNote}
                          onChange={(event) =>
                            setConfirmNote(event.target.value)
                          }
                          placeholder="Why this cost is correct"
                        />
                      </label>
                    </div>
                    <div className="cardcam-confirm-actions">
                      <small>
                        Use £0 grading when the slab was bought already graded.
                      </small>
                      <button type="submit" disabled={confirming}>
                        {confirming
                          ? 'Confirming…'
                          : `Confirm revision ${selected.revision}`}
                      </button>
                    </div>
                    {confirmError ? (
                      <p className="cardcam-confirm-error">{confirmError}</p>
                    ) : null}
                  </form>
                ) : null}
                {canConfirm && selected.reviewStatus === 'confirmed' ? (
                  <p className="cardcam-confirm-success">
                    {confirmMessage ||
                      `Tom confirmed this card at revision ${selected.revision}.`}
                  </p>
                ) : null}
                </div>
              </details>
            ) : null}

            {isAdminView ? (
              <section className="cardcam-section cardcam-history-section">
                <div className="cardcam-section-heading">
                  <span>Finalized CardCam sale history</span>
                  <small>Exact printing · latest five sales</small>
                </div>
                {saleHistory.loading ? (
                  <p className="cardcam-history-state">
                    Loading exact sale history…
                  </p>
                ) : saleHistory.error ? (
                  <p className="cardcam-history-state cardcam-history-error">
                    {saleHistory.error}
                  </p>
                ) : saleHistory.analytics ? (
                  <>
                    <div className="cardcam-metrics">
                      <Metric
                        label="Last hammer"
                        value={formatGBP(
                          saleHistory.analytics.lastFive?.[0]?.hammerGBP
                        )}
                        detail="Most recent finalized sale"
                      />
                      <Metric
                        label="5-sale average (mean)"
                        value={formatGBP(
                          saleHistory.analytics.meanHammerGBP
                        )}
                        detail={`${saleHistory.analytics.sampleCount} of ${
                          saleHistory.analytics.totalCount
                        } recorded sale${
                          saleHistory.analytics.totalCount === 1 ? '' : 's'
                        }`}
                      />
                      <Metric
                        label="5-sale median"
                        value={formatGBP(
                          saleHistory.analytics.medianHammerGBP
                        )}
                        detail="Less affected by one unusual result"
                      />
                      <Metric
                        label="Finalized sales"
                        value={String(saleHistory.analytics.totalCount)}
                        detail="Paid, non-giveaway, non-refunded"
                      />
                    </div>
                    {saleHistory.analytics.lastFive?.length ? (
                      <ol className="cardcam-history-list">
                        {saleHistory.analytics.lastFive.map((sale) => (
                          <li key={sale.captureID}>
                            <span>
                              <strong>{formatGBP(sale.hammerGBP)}</strong>
                              <small>
                                {sale.sellerUsername} · {formatTime(sale.soldAt)}
                              </small>
                            </span>
                            <span>
                              <b>{formatGBP(sale.netProfitGBP)}</b>
                              <small>Admin net profit</small>
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="cardcam-history-state">
                        No finalized sale exists for this exact printing yet.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="cardcam-history-state">
                    Exact sale history is unavailable until the card has a
                    canonical identity.
                  </p>
                )}
              </section>
            ) : null}

            {selected.warnings.length ? (
              <section className="cardcam-warnings">
                <strong>Checks</strong>
                <ul>
                  {selected.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </section>
            ) : null}
          </article>
        </div>
      )}
    </div>
  );
}
